import type { FoodId } from './foods';
import { FOOD_CATALOG } from './foods';
import { TOY_CATALOG, type ToyId } from './toys';

export interface CareProfile {
  healthyMeals: number;
  snacks: number;
  plays: number;
  cleanups: number;
  lateCleanups: number;
  heals: number;
  sickTimeMs: number;
  correctDisciplines: number;
  wrongDisciplines: number;
  tantrumsResolved: number;
  refusals: number;
  heartyMeals: number;
  activePlays: number;
  comfortPlays: number;
  focusPlays: number;
  lastToyId: ToyId | null;
  consecutiveToyUses: number;
}

export type CareEventForProfile =
  | { kind: 'food'; foodId: FoodId }
  | { kind: 'play'; toyId: ToyId }
  | { kind: 'clean'; late: boolean }
  | { kind: 'heal' }
  | { kind: 'discipline'; correct: boolean; resolvedTantrum?: boolean }
  | { kind: 'refusal' }
  | { kind: 'sick-time'; dtMs: number };

export function createCareProfile(): CareProfile {
  return {
    healthyMeals: 0,
    snacks: 0,
    plays: 0,
    cleanups: 0,
    lateCleanups: 0,
    heals: 0,
    sickTimeMs: 0,
    correctDisciplines: 0,
    wrongDisciplines: 0,
    tantrumsResolved: 0,
    refusals: 0,
    heartyMeals: 0,
    activePlays: 0,
    comfortPlays: 0,
    focusPlays: 0,
    lastToyId: null,
    consecutiveToyUses: 0,
  };
}

export function normalizeCareProfile(profile?: Partial<CareProfile> | null): CareProfile {
  return { ...createCareProfile(), ...(profile ?? {}) };
}

export function recordCare(profile: CareProfile, event: CareEventForProfile): CareProfile {
  const next = { ...profile };
  switch (event.kind) {
    case 'food': {
      const category = FOOD_CATALOG[event.foodId].category;
      if (category === 'snack') next.snacks += 1;
      else if (category === 'hearty') next.heartyMeals += 1;
      else next.healthyMeals += 1;
      return next;
    }
    case 'play': {
      next.plays += 1;
      const style = TOY_CATALOG[event.toyId].style;
      if (style === 'active') next.activePlays += 1;
      if (style === 'comfort') next.comfortPlays += 1;
      if (style === 'focus') next.focusPlays += 1;
      next.consecutiveToyUses = next.lastToyId === event.toyId ? next.consecutiveToyUses + 1 : 1;
      next.lastToyId = event.toyId;
      return next;
    }
    case 'clean':
      next.cleanups += 1;
      if (event.late) next.lateCleanups += 1;
      return next;
    case 'heal':
      next.heals += 1;
      return next;
    case 'discipline':
      if (event.correct) next.correctDisciplines += 1;
      else next.wrongDisciplines += 1;
      if (event.resolvedTantrum) next.tantrumsResolved += 1;
      return next;
    case 'refusal':
      next.refusals += 1;
      return next;
    case 'sick-time':
      next.sickTimeMs += Math.max(0, event.dtMs);
      return next;
  }
}
